import { type PayloadAction } from '@reduxjs/toolkit';

import { type PickPropsAsNullable } from '@pkg/types';

import { type PagingParams, type ResourceId } from '@app/client/common/structs';
import { type YatPageResponse, type YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';

import { type NFTItem } from '../../types/nft-manager';
import { type ContentItem, ContentType, type NFTContent } from '../../types/yat-page-actions';

// TODO: Rename this to `YatPagePublishStatus`
export enum PublishState {
    Published,
    Modified,
    Unchanged,
}

export interface YatPageEditStates {
    // TODO: Consider changing to `title` and `description`.
    editingHeading?: boolean;
    editingBio?: boolean;
    editingSocial?: boolean;
    editingItem?: boolean;
    editingLiquidID?: boolean;
}

export enum LiquidIDEditStage {
    Empty,
    Selection,
    Added,
}

// TODO: Can `PickPropsAsNullable<>` just use `Partial<>` instead?
// Why do we actually care about `YatPageResponse` having null values?
// This appears to be JUST for setting the initial state.
type NullableYatPageResponse = PickPropsAsNullable<YatPageResponse, keyof YatPageResponse>;

export interface YatPageState extends NullableYatPageResponse {
    pageAssets: PageAssets;
    pageItemsPersisted: PersistedItems;
    liquidIDItems: (NFTItem | undefined)[];
    isLoadingItems?: boolean;
    cantPublish?: boolean;
    liquidIDSelectedItem?: NFTItem;
    liquidIDEditStage: LiquidIDEditStage;
    liquidIDFaceSelection: number | undefined;
    liquidIDExpandedPreview: boolean;
    liquidIDVisibleFace: number;
    liquidIDLoading?: boolean;
    liquid_id: ResourceId | null;
    // TODO: Consider renaming this prop to `status`
    publishedState: PublishState;
    yatPageVersion: YatPageVersion | null;
    editStates: YatPageEditStates;
    draggingId?: string;
    draggingLiquidIDThumbnail?: string;
    paging?: PagingParams;
    editingItemId?: ContentItem['id'];
    previewItem?: ContentItem<NFTContent>;
    hideAddItemUi?: boolean;
    autoProfileSetting?: boolean;
    replacedIds: ReplacedIds[];
    invalidLink?: string;
    isPageOwner?: boolean;
}

interface ReplacedIds {
    tempId: string;
    newId: string;
}

interface PersistedItems {
    pageItems: ContentItem[];
    liquidIdItems: LiquidIDItem[];
}

interface PageAsset {
    asset: ContentItem;
    index: number;
}

interface PageAssetsData {
    [id: string]: PageAsset;
}

export interface PageAssets {
    draft: PageAssetsData;
    modified: string[];
    published: PageAssetsData;
    sorted: string[];
    toggled: {
        [id: string]: 'added' | 'removed';
    };
}

export type LiquidIDItem = NFTItem | undefined;
interface LiquidIDItemAction {
    items: LiquidIDItem[];
    unchanged?: boolean;
}

// TODO: Should there be a distinction between these types?
// If not, then we don't really need a separate `public` function.
export type SetPageAction = PayloadAction<NullableYatPageResponse>;
export type SetPublicPageAction = PayloadAction<NullableYatPageResponse>;

export type SetItemsAction = PayloadAction<ContentItem[]>;
export type SetLiquidIdItemsPersistedAction = PayloadAction<LiquidIDItem[]>;
export type SetPagingAction = PayloadAction<PagingParams>;
export type SetVersionAction = PayloadAction<YatPageVersion>;
export type SetIsOwnerAction = PayloadAction<boolean>;
export type SetIsLoadingItems = PayloadAction<boolean>;

// TODO: These should be named `title` and `description`.
export type SetHeadingAction = PayloadAction<string>;
export type SetBioAction = PayloadAction<string>;
export type SetLiquidIDAction = PayloadAction<LiquidIDItemAction>;
export type SetLiquidIdEditStage = PayloadAction<LiquidIDEditStage>;
export type AddLiquidIDItemAction = PayloadAction<{ item: LiquidIDItem; face: number | undefined }>;
export type StageLiquidIDItemAction = PayloadAction<LiquidIDItem>;
export type ReorderLiquidIDAction = PayloadAction<{ from: number; to: number }>;
export type setLiquidIDFaceSelectionAction = PayloadAction<number | undefined>;
export type SetLiquidIDExpandedPreviewAction = PayloadAction<boolean>;
export type SetLiquidIDVisibleFaceAction = PayloadAction<number>;
export type SetLiquidIDLoadingAction = PayloadAction<boolean>;

export type SetDraggingAction = PayloadAction<YatPageState['draggingId']>;
export type SetEditingItemAction = PayloadAction<YatPageState['editingItemId']>;
export type SetPreviewItemAction = PayloadAction<YatPageState['previewItem']>;

export type UpdateEditStatesAction = PayloadAction<YatPageEditStates>;

export type AddItemAction = PayloadAction<ContentItem>;
export type AddBulkItemsAction = PayloadAction<ContentItem[]>;
export type UpdateItemAction = PayloadAction<ContentItem>;
export type UpdateItemsOrderAction = PayloadAction<ContentItem['id']>;
export type RemoveItemAction = PayloadAction<ContentItem['id']>;
export type RemoveBulkItemsAction = PayloadAction<ContentItem['id'][]>;
export type ReplaceItemTempIdAction = PayloadAction<{ tempId: ResourceId; id: ResourceId }>;

/**
 * isContentItem: is used to narrow the type on an array filter for undefined items in an array of ContentItem.
 */
export const isContentItem = (item: ContentItem | undefined): item is ContentItem => {
    return !!item;
};
/**
 * isContentItem: is used as a [type predicate](https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates)
 */
export const isContentItemNft = (item?: ContentItem): item is ContentItem<NFTContent> => item?.type === ContentType.NFT;

/**
 * isContentItemPublished: is used to filter out items that have not been published yet
 */
export const isContentItemPublished = (item: ContentItem | undefined): item is ContentItem => {
    return !!item?.published;
};
